import React, { FC, useCallback, useMemo, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { StockSliceProps, MAX_STOCK_SLICE_PODS_COUNT } from '.';
import LocationInput from '../../FilterBox/Components/LocationInput';
import { StyledGrid } from '@components/Grid';
import TrimSelectorDuck from '../../../redux/trimSelector/trimSelector.duck';
import { buildParameterizedPathname } from '@utils/url.utils';
import routes from '../../../constants/routes';
import { CarJourneyType } from '../../../services';
import { SortOrder, SortType } from '../../../services/stock/models/stockItems.service.model';
import { useCarDetailsDuck } from '@hooks/useCarDetailsDuck';
import { useCustomRouter } from '@hooks/useCustomRouter';
import { useUIDuck } from '@hooks/useUIDuck';
import FilterDuck from '../../../redux/filters/filter.duck';
import TrimCarousel from '@components/TrimCarousel';
import useTranslations from '@hooks/useTranslations';
import { BRAND } from '../../../constants/main';
import { useCarStockJourneyCheck } from '@hooks/useCarStockJourneyCheck';
import StockService from '../../../services/stock/stock.service';
import { INITIAL_PAGE_NUMBER } from '../../../pages/trim/stock/[...model]';
import Spinner from '@components/Spinner';
import UIDuck from '../../../redux/commonDucks/ui.duck';
import { getLanguageLongString } from '@utils/getLanguageLongString';
import { useTranslation } from '../../../../i18n';
import { Icons } from '@components/Icon';
import Icon from '@components/Icon';
import { Redux } from 'src/redux/redux.interface';
import Button from "@components/Button";

export const StockSliceTemplate: FC<StockSliceProps> = ({ className, normalizeEnabled }) => {
    const { t } = useTranslations();
    const router = useCustomRouter();
    const dispatch = useDispatch();
    const filteredStockTrims = useSelector((state: Redux) => TrimSelectorDuck.getFilteredStockTrims(state));
    const totalItems = useSelector((state: Redux) => TrimSelectorDuck.getFilteredStockTrimsCount(state));
    const geoLocation = useSelector((state: Redux) => FilterDuck.getGeoLocation(state));
    const distanceRadiusValue = useSelector((state: Redux) => FilterDuck.getDistanceRadius(state));
    const { isStockJourney } = useCarStockJourneyCheck();
    const { carDetails } = useCarDetailsDuck();
    const { isLoadingNow } = useUIDuck();
    const { i18n } = useTranslation();
    const language = getLanguageLongString(i18n.language);

    const refreshTrims = async () => {
        const responseAllStockSlices = await StockService.getStockVehicles(
            carDetails.model.id,
            carDetails.bodyStyle.id,
            INITIAL_PAGE_NUMBER,
            MAX_STOCK_SLICE_PODS_COUNT,
            {}
        );

        const energies = responseAllStockSlices.data?.filters?.energies || [];
        const fuelCode = energies.find(
            (engineFuel: { labels: { [x: string]: any } }) => engineFuel.labels[language] === carDetails.fuel
        );

        if (isLoadingNow === false) {
            dispatch(UIDuck.setIsLoadingNow(true));
            if (fuelCode) {
                const { data } = await StockService.getStockVehicles(
                    carDetails.model.id,
                    carDetails.bodyStyle.id,
                    INITIAL_PAGE_NUMBER,
                    MAX_STOCK_SLICE_PODS_COUNT,
                    {
                        energies: fuelCode.value,
                        grades: carDetails.specPack.id,
                    },
                    geoLocation,
                    distanceRadiusValue
                );
                dispatch(
                    TrimSelectorDuck.setStockTrims({
                        stockTrims: data?.vehicles,
                        totalItems: data?.totalVehiclesCount,
                        pageNumber: INITIAL_PAGE_NUMBER,
                        filteredStockTrims: data?.vehicles,
                        totalFilteredItems: data?.totalVehiclesCount,
                    })
                );
            } else {
                dispatch(
                    TrimSelectorDuck.setStockTrims({
                        stockTrims: [],
                        totalItems: 0,
                        pageNumber: INITIAL_PAGE_NUMBER,
                        filteredStockTrims: [],
                        totalFilteredItems: 0,
                    })
                );
            }
            dispatch(UIDuck.setIsLoadingNow(false));

            return;
        }
    };

    useEffect(() => {
        refreshTrims();
    }, [geoLocation, carDetails?.model?.id]);

    //for MTO journey we are disabling radius selector and always setting national wide search = 0
    if (!isStockJourney && Number(distanceRadiusValue) !== 0) {
        dispatch(FilterDuck.setDistanceRadius(0));
    }
    const geoLocationName = useSelector((state: Redux) => FilterDuck.getGeoLocationName(state));
    const areGeoLocationParamsDefined =
        geoLocation && typeof distanceRadiusValue === 'number' && Boolean(geoLocationName);

    const query = typeof window !== 'undefined' ? router.query : router.query;

    const offerUrl = useMemo(() => {
        const pathname = buildParameterizedPathname(routes.SELECTOR, CarJourneyType.STOCK, {
            nameplateBodyStyleSlug: filteredStockTrims?.[0]?.nameplateBodyStyleSlug,
            modelId: filteredStockTrims?.[0]?.model.id,
            bodyStyleId: filteredStockTrims?.[0]?.bodyStyle.id,
        });

        const fuel = filteredStockTrims?.[0]?.fuel;
        const specPackDesc = filteredStockTrims?.[0]?.specPack.title;
        const gradesAndEnergiesFilter = `?grades=${specPackDesc}&energies=${fuel}`;

        let searchString = '';
        if (areGeoLocationParamsDefined) {
            searchString = searchString.includes(`sort=${SortType.PRICE}`)
                ? searchString.replace(SortType.PRICE, SortType.PROXIMITY)
                : `&sort=${SortType.PROXIMITY}:${SortOrder.ASC}`;
        }

        return `${pathname}${gradesAndEnergiesFilter}${searchString}`;
    }, [query, router]);

    // redirect to offer logic
    const handleShowOfferClick = useCallback(
        (event: React.MouseEvent<HTMLAnchorElement | HTMLButtonElement | HTMLHeadingElement, MouseEvent>) => {
            event.preventDefault();

            router.push(offerUrl);
        },
        [offerUrl]
    );

    return (
        <div className={className} id="stock-slice">
            {filteredStockTrims.length > 0 && (
                <StyledGrid brand={BRAND} style={{ padding: 'unset' }}>
                    <div className="header">
                        <div className="header-title">
                            <p className="header-title-main">{t('configurator.stockSlice.title')}</p>
                        </div>
                        <div className="header-location">
                            <div className="header-location-input">
                                <LocationInput isStockSlice />
                                <p className="header-title-description">
                                    <strong>{totalItems}</strong> {t('configurator.stockSlice.description')}
                                </p>
                            </div>
                            <Button onClick={handleShowOfferClick} secondary>
                                {t('configurator.stockSlice.button')}
                            </Button>
                        </div>
                    </div>
                    <div className="vehicles">
                        {isLoadingNow ? (
                            <div className="dealerSpinner">
                                <Spinner size={25} border={3} color="#000000" />
                            </div>
                        ) : (
                            <TrimCarousel
                                filteredTrims={filteredStockTrims as any}
                                filteredOutTrims={[]}
                                trimConfigurations={{}}
                                isStockSliceCarousel
                                normalizeEnabled={normalizeEnabled}
                            />
                        )}
                    </div>
                </StyledGrid>
            )}
        </div>
    );
};

export default StockSliceTemplate;
